import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { BrickSettingsService, CallHistoryBrickSettingRemoteDto } from '../../../../../../../generated/swagger';
import { IBrickSettingStateModel, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import {
   CallHistoryChangeAutomaticFilteringOnIncomingCall,
   CallHistoryChangeRetainFilterOnCallFinish,
   CallHistoryChangeShowPrimaryFullDateFormat,
} from './call-history-browser-settings.actions';

export interface CallHistoryBrickSettingsStateModel extends IBrickSettingStateModel<CallHistoryBrickSettingRemoteDto> {}

function getDefaultSpecificBrickSetting(): CallHistoryBrickSettingRemoteDto {
   return {
      commonBrickSettingRemoteDto: null,
      automaticFilteringOnIncomingCall: false,
      retainFilterOnCallFinish: true,
      showPrimaryFullDateFormat: false,
   };
}

@State<CallHistoryBrickSettingsStateModel>({
   name: 'callHistoryBrickSettingsState',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
   },
})
@Injectable()
export class CallHistoryBrowserSettingsState extends SpecificBrickSettingsStateBase<CallHistoryBrickSettingRemoteDto>(getDefaultSpecificBrickSetting) {
   constructor(private brickSettingsService: BrickSettingsService, store: Store, actions$: Actions) {
      super(store);
   }

   @Selector([CallHistoryBrowserSettingsState, CallHistoryBrowserSettingsState.getSettingModelFn])
   public static getAutomaticFilteringOnIncomingCall(state, getSettingModelFn: (state, brickInstanceId) => CallHistoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).automaticFilteringOnIncomingCall;
      };
   }

   @Selector([CallHistoryBrowserSettingsState, CallHistoryBrowserSettingsState.getSettingModelFn])
   public static getRetainFilterOnCallFinish(state, getSettingModelFn: (state, brickInstanceId) => CallHistoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).retainFilterOnCallFinish;
      };
   }

   @Selector([CallHistoryBrowserSettingsState, CallHistoryBrowserSettingsState.getSettingModelFn])
   public static getShowPrimaryFullDateFormat(state, getSettingModelFn: (state, brickInstanceId) => CallHistoryBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showPrimaryFullDateFormat;
      };
   }

   @Action(CallHistoryChangeRetainFilterOnCallFinish)
   @ImmutableContext()
   public callHistoryChangeRetainFilterOnCallFinish(ctx: StateContext<CallHistoryBrickSettingsStateModel>, action: CallHistoryChangeRetainFilterOnCallFinish) {
      ctx.setState((state: CallHistoryBrickSettingsStateModel) => {
         const brickSettingModel: CallHistoryBrickSettingRemoteDto = CallHistoryBrowserSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.retainFilterOnCallFinish = action.retainFilterOnCallFinish;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(CallHistoryChangeAutomaticFilteringOnIncomingCall)
   @ImmutableContext()
   public callHistoryChangeAutomaticFilteringOnIncomingCall(
      ctx: StateContext<CallHistoryBrickSettingsStateModel>,
      action: CallHistoryChangeAutomaticFilteringOnIncomingCall
   ) {
      ctx.setState((state: CallHistoryBrickSettingsStateModel) => {
         const brickSettingModel: CallHistoryBrickSettingRemoteDto = CallHistoryBrowserSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.automaticFilteringOnIncomingCall = action.automaticFilteringOnIncomingCall;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(CallHistoryChangeShowPrimaryFullDateFormat)
   @ImmutableContext()
   public CallHistoryChangeShowPrimaryFullDateFormat(
      ctx: StateContext<CallHistoryBrickSettingsStateModel>,
      action: CallHistoryChangeShowPrimaryFullDateFormat
   ) {
      ctx.setState((state: CallHistoryBrickSettingsStateModel) => {
         const brickSettingModel: CallHistoryBrickSettingRemoteDto = CallHistoryBrowserSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.showPrimaryFullDateFormat = action.showPrimaryFullDateFormat;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: CallHistoryBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeCallHistoryBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }
}
